import clsx from 'clsx';
import type { FunctionComponent } from 'react';
import { createPortal } from 'react-dom';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { IconChatBubble7 } from '~/ui/core/icons';
import { floatingSurfaceClassNames, floatingSurfaceItemClassNames } from '~/ui/core/surface/surfaceClasses';

import { COMMENT_BUTTON_ATTRIBUTE } from './anchor/grammar';

const SELECTION_BUTTON_OFFSET_PX = 6;

type SelectionCommentButtonProps = {
    readonly rect: DOMRect;
    readonly onComment: () => void;
};

export const SelectionCommentButton: FunctionComponent<SelectionCommentButtonProps> = ({ rect, onComment }) => {
    const translate = useTranslate();

    return createPortal(
        <div
            className="motion-safe:animate-fade-in fixed z-[9999]"
            {...{ [COMMENT_BUTTON_ATTRIBUTE]: true }}
            data-testid="selection-comment-button"
            style={{
                top: rect.bottom + SELECTION_BUTTON_OFFSET_PX,
                left: rect.left + rect.width / 2,
                transform: 'translateX(-50%)',
                willChange: 'opacity',
            }}
        >
            <div
                className={clsx(
                    floatingSurfaceClassNames.tone.gray,
                    'font-sans rounded-xl overflow-hidden p-xs min-w-[7rem]',
                )}
            >
                <Button
                    className={clsx(
                        'flex w-full items-center gap-xs rounded-sm py-s px-m text-left',
                        'appearance-none border-0 bg-transparent font-sans',
                        'select-none text-text-secondary text-body-xxs font-normal',
                        '[&_svg]:text-text-content-faint',
                        floatingSurfaceItemClassNames.hover,
                    )}
                    onClick={onComment}
                    variant="plain"
                    onMouseDown={(event) => event.preventDefault()}
                >
                    <IconChatBubble7 size={12} />
                    {translate('pages.job-architecture-v2.comments.selection-button.label')}
                </Button>
            </div>
        </div>,
        document.body,
    );
};
